import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { Club, clubsApi } from '../api/clients/clubs';
import { setTenantId } from '../api/config';
import { setSentryContext } from '../utils/sentry';
import { useAuth } from './AuthContext';
import { safeGetItem, safeSetItem } from '../utils/storage';

interface TenantState {
  activeClub: Club | null;
  availableClubs: Club[];
  isPlatformAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

type TenantAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: { clubs: Club[]; isPlatformAdmin: boolean; lastClubId?: string } }
  | { type: 'LOAD_ERROR'; payload: string }
  | { type: 'SET_ACTIVE_CLUB'; payload: Club }
  | { type: 'CLEAR_ERROR' };

interface TenantContextType extends TenantState {
  setActiveClub: (club: Club) => void;
  refreshClubs: () => Promise<void>;
  clearError: () => void;
  applyTheme: (club: Club) => void;
  setDefaultClubId: (clubId: string) => Promise<void> | void;
}

export const TenantContext = createContext<TenantContextType | undefined>(undefined);

const STORAGE_KEY = 'tenant_last_club_id';
const DEFAULT_KEY = 'tenant_default_club_id';

const tenantReducer = (state: TenantState, action: TenantAction): TenantState => {
  switch (action.type) {
    case 'LOAD_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOAD_SUCCESS':
      const { clubs, isPlatformAdmin, lastClubId } = action.payload;
      let activeClub = clubs.length > 0 ? clubs[0] : null;
      
      if (lastClubId) {
        const lastClub = clubs.find(c => c.id === lastClubId);
        if (lastClub) {
          activeClub = lastClub;
        }
      }
      
      return {
        ...state,
        availableClubs: clubs,
        activeClub,
        isPlatformAdmin,
        isLoading: false,
        error: null,
      };
    case 'LOAD_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'SET_ACTIVE_CLUB':
      return {
        ...state,
        activeClub: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

const initialState: TenantState = {
  activeClub: null,
  availableClubs: [],
  isPlatformAdmin: false,
  isLoading: true,
  error: null,
};

interface TenantProviderProps {
  children: ReactNode;
}

const getIndustryFallbackTheme = (industry: string) => {
  const themes: Record<string, { primary: string; secondary: string; accent: string }> = {
    fitness: {
      primary: '#FF6B35',
      secondary: '#004E89',
      accent: '#F7B801',
    },
    sports: {
      primary: '#1E88E5',
      secondary: '#43A047',
      accent: '#FDD835',
    },
    martial_arts: {
      primary: '#D32F2F',
      secondary: '#212121',
      accent: '#FFC107',
    },
    default: {
      primary: '#3B82F6',
      secondary: '#1E293B',
      accent: '#10B981',
    },
  };
  
  return themes[industry] || themes.default;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(tenantReducer, initialState);
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();

  const applyTheme = useCallback((club: Club) => {
    const root = document.documentElement;
    const branding = club.branding ?? ({} as any);

    const fallback = getIndustryFallbackTheme(club.industry);

    root.style.setProperty('--color-primary', branding.primary_color || fallback.primary);
    root.style.setProperty('--color-secondary', branding.secondary_color || fallback.secondary);
    root.style.setProperty('--color-accent', branding.accent_color || fallback.accent);
    root.style.setProperty('--font-family', branding.font_family || 'system-ui, sans-serif');
    root.style.setProperty('--font-heading', branding.font_heading || branding.font_family || 'system-ui, sans-serif');

    if (branding.logo_url) {
      root.style.setProperty('--logo-url', `url(${branding.logo_url})`);
    } else {
      root.style.removeProperty('--logo-url');
    }
  }, []);

  const loadClubs = useCallback(async () => {
    dispatch({ type: 'LOAD_START' });
    try {
      console.log('[TenantContext] Loading clubs from /api/me/clubs');
      const response = await clubsApi.getMyClubs();
      console.log('[TenantContext] Clubs loaded successfully:', response.clubs.length, 'clubs');
      const lastClubId = safeGetItem(STORAGE_KEY) || undefined;
      const defaultClubId = (user?.default_club_id || safeGetItem(DEFAULT_KEY) || undefined) as string | undefined;
      
      dispatch({
        type: 'LOAD_SUCCESS',
        payload: {
          clubs: response.clubs,
          isPlatformAdmin: response.is_platform_admin,
          lastClubId,
        },
      });
      
      // Determine preferred club: default > last > single-club auto, else none
      const clubFromDefault = defaultClubId ? response.clubs.find(c => c.id === defaultClubId) : undefined;
      const clubFromLast = lastClubId ? response.clubs.find(c => c.id === lastClubId) : undefined;
      const clubToApply = clubFromDefault || clubFromLast || (response.clubs.length === 1 ? response.clubs[0] : undefined);
      
      if (clubToApply) {
        applyTheme(clubToApply);
        // Set tenant ID for API requests
        setTenantId(clubToApply.id);
        // Persist last used for convenience
        safeSetItem(STORAGE_KEY, clubToApply.id);
      }
    } catch (error: any) {
      console.error('[TenantContext] Failed to load clubs:', error?.response?.status, error?.response?.data);
      const message = error?.response?.data?.error?.message || 'Failed to load clubs';
      dispatch({ type: 'LOAD_ERROR', payload: message });
    }
  }, [applyTheme, user?.default_club_id]);

  useEffect(() => {
    // Only load clubs after auth is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      console.log('[TenantContext] Auth confirmed, loading clubs');
      loadClubs();
    } else if (!authLoading && !isAuthenticated) {
      console.log('[TenantContext] Not authenticated, skipping club load');
      dispatch({ type: 'LOAD_ERROR', payload: 'Not authenticated' });
    } else {
      console.log('[TenantContext] Waiting for auth to complete');
    }
  }, [authLoading, isAuthenticated, loadClubs]);

  const setActiveClub = useCallback((club: Club) => {
    dispatch({ type: 'SET_ACTIVE_CLUB', payload: club });
    safeSetItem(STORAGE_KEY, club.id);
    applyTheme(club);
    // Set tenant ID for API requests (club.id is the Stripe account ID)
    setTenantId(club.id);
    
    // Set Sentry tenant context
    setSentryContext('tenant', {
      id: club.id,
      name: club.name,
      industry: club.industry,
      role: club.role,
    });
  }, [applyTheme]);

  const setDefaultClubId = useCallback(async (clubId: string) => {
    // Placeholder for backend persistence. For now, store locally as a fallback.
    const stored = safeSetItem(DEFAULT_KEY, clubId);
    if (stored) {
      console.log('[TenantContext] Default club stored locally:', clubId);
    } else {
      console.warn('[TenantContext] Failed to persist default club locally');
    }
  }, []);

  const refreshClubs = useCallback(async () => {
    await loadClubs();
  }, [loadClubs]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return (
    <TenantContext.Provider
      value={{
        ...state,
        setActiveClub,
        refreshClubs,
        clearError,
        applyTheme,
        setDefaultClubId,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
